import React, { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { routes } from "consts";
import { HeaderWithBreadcrumbs } from "components/molecules/HeaderWithBreadcrumbs/HeaderWithBreadbrumbs";
import { PageMain } from "components/molecules";
import { PaziaBox, PaziaButton, PaziaLoader } from "components/atoms";
import { useToaster } from "utils";
import { ksefApi, useKsefIntegration, KSeFIntegrationApi } from "api/ksef";
import { KSeFIntegrationForm, KSeFIntegrationFormValues } from "./KSeFIntegrationForm";

export const KSeFEditPage = (): JSX.Element => {
  const { ksefId } = useParams();
  const id = Number(ksefId);
  const { isLoading, integration, invalidate } = useKsefIntegration(id);
  const [editMode, setEditMode] = React.useState(false);
  const { showSuccess, showError } = useToaster();

  const onSubmit = async (data: KSeFIntegrationFormValues) => {
    try {
      await ksefApi.update(id, data);
      showSuccess("Zapisano zmiany integracji KSeF");
      setEditMode(false);
      invalidate();
    } catch (e) {
      showError("Nie udało się zapisać integracji KSeF");
      console.error(e);
    }
  };

  const onTestConnection = async () => {
    try {
      const result = await ksefApi.testConnection(id);
      if (result.data.success) {
        showSuccess("Połączenie z KSeF działa poprawnie");
      } else {
        showError("Połączenie z KSeF nie powiodło się");
      }
    } catch (e) {
      showError("Nie udało się przetestować połączenia z KSeF");
      console.error(e);
    }
  };

  const onCertificateUploaded = () => {
    invalidate();
  };

  return (
    <PageMain>
      <HeaderWithBreadcrumbs
        breadcrumbs={[{ title: "KSeF", path: routes.KSEF }, { title: "Edycja integracji" }]}
        headerText="Edycja integracji KSeF"
      />
      {isLoading && <PaziaLoader position="center" />}
      {integration && (
        <div className="flex flex-col gap-4">
          {editMode ? (
            <PaziaBox onBack={() => setEditMode(false)} header="Dane integracji">
              <KSeFIntegrationForm onSubmit={onSubmit} data={integration} />
            </PaziaBox>
          ) : (
            <IntegrationPreview integration={integration} onEdit={() => setEditMode(true)} onTest={onTestConnection} />
          )}
          <CertificateUploadSection integrationId={id} integration={integration} onUploaded={onCertificateUploaded} />
        </div>
      )}
    </PageMain>
  );
};

const IntegrationPreview = ({
  integration,
  onEdit,
  onTest,
}: {
  integration: KSeFIntegrationApi;
  onEdit: () => void;
  onTest: () => void;
}) => {
  return (
    <PaziaBox onEdit={onEdit} header="Dane integracji">
      <div className="flex flex-col gap-2">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <span className="font-medium text-slate-500">Nazwa</span>
          <span>{integration.name}</span>
          <span className="font-medium text-slate-500">Środowisko</span>
          <span>{integration.environment === 1 ? "Test" : "Produkcja"}</span>
          <span className="font-medium text-slate-500">BaseUrl</span>
          <span>{integration.baseUrl}</span>
          <span className="font-medium text-slate-500">NIP</span>
          <span>{integration.nip}</span>
          <span className="font-medium text-slate-500">Typ autoryzacji</span>
          <span>{integration.authType === "token" ? "Token KSeF" : "Podpis kwalifikowany (XAdES)"}</span>
          <span className="font-medium text-slate-500">Status</span>
          <span className={integration.isEnabled ? "text-green-600" : "text-red-500"}>
            {integration.isEnabled ? "Aktywna" : "Nieaktywna"}
          </span>
          {integration.sellerName && (
            <>
              <span className="font-medium text-slate-500">Sprzedawca</span>
              <span>{integration.sellerName}</span>
            </>
          )}
        </div>
        <div className="mt-4">
          <PaziaButton type="secondary" title="Testuj połączenie" onClick={onTest} />
        </div>
      </div>
    </PaziaBox>
  );
};

const CertificateUploadSection = ({
  integrationId,
  integration,
  onUploaded,
}: {
  integrationId: number;
  integration: KSeFIntegrationApi;
  onUploaded: () => void;
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const { showSuccess, showError } = useToaster();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showError("Wybierz plik certyfikatu (.pfx)");
      return;
    }
    if (!password) {
      showError("Podaj hasło certyfikatu");
      return;
    }
    setIsUploading(true);
    try {
      await ksefApi.uploadCertificate(integrationId, selectedFile, password);
      showSuccess("Certyfikat wgrany pomyślnie. Token KSeF został wygenerowany i zapisany.");
      setSelectedFile(null);
      setPassword("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      onUploaded();
    } catch (e) {
      showError("Nie udało się wgrać certyfikatu. Sprawdź plik i hasło.");
      console.error(e);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <PaziaBox header="Certyfikat KSeF">
      <div className="flex flex-col gap-4">
        {integration.hasCertificate && (
          <div className="flex items-center gap-2 rounded-md bg-green-50 px-3 py-2 text-sm text-green-700 dark:bg-green-900/20 dark:text-green-400">
            <span className="text-green-500">✓</span>
            <span>Certyfikat załadowany</span>
          </div>
        )}

        <p className="text-sm text-slate-500">
          Wgraj plik certyfikatu (.pfx / .p12) w celu uwierzytelnienia w KSeF za pomocą podpisu XAdES. Po wgraniu
          zostanie automatycznie wygenerowany token KSeF i zapisany w konfiguracji.
        </p>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Plik certyfikatu (.pfx / .p12)</label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pfx,.p12"
              onChange={handleFileChange}
              className="block w-full cursor-pointer rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 file:mr-3 file:cursor-pointer file:rounded file:border-0 file:bg-primary-50 file:px-3 file:py-1 file:text-xs file:font-medium file:text-primary-700 hover:file:bg-primary-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
            />
            {selectedFile && (
              <span className="text-xs text-slate-500">
                {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
              </span>
            )}
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Hasło certyfikatu</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Hasło do pliku .pfx"
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-primary-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300"
            />
          </div>

          <PaziaButton
            type="primary"
            title={isUploading ? "Wgrywanie..." : "Wgraj i generuj token"}
            onClick={handleUpload}
            disabled={isUploading || !selectedFile || !password}
          />
        </div>
      </div>
    </PaziaBox>
  );
};
